import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "services/api";
import { Collapse } from "react-bootstrap";
import { useHistory, Link } from "react-router-dom";
import Translator, { __ } from "services/Translator";
import { getProviderSlug, normalizeProviders } from "utils/providers";
import { searchCategories } from "services/categories";

const DEFAULT_GAME_IMAGE = require("assets/play365/images/games/default.png");

const SearchBox = (props) => {
  const history = useHistory();
  const [searchStr, setSearchStr] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const searchTextBox = useRef(null);
  const suggestionsListRef = useRef(null);
  const rootRef = useRef(null);
  const enabledProviders = normalizeProviders(Array.isArray(props.providers) ? props.providers : []);

  const enabledProviderIds = new Set(
    enabledProviders
      .map((provider) => String(provider.provider_id || "").trim())
      .filter(Boolean)
  );

  const enabledProviderSlugs = new Set(
    enabledProviders
      .map((provider) => String(getProviderSlug(provider) || "").toLowerCase().trim())
      .filter(Boolean)
  );

  const enabledProviderNames = new Set(
    enabledProviders
      .map((provider) => String(provider.name || "").toLowerCase().trim())
      .filter(Boolean)
  );

  const isEnabledProviderSuggestion = (item) => {
    if (!enabledProviders.length) return true;

    const provider = item?.provider || {};
    const candidateIds = [
      item?.provider_id,
      item?.providerId,
      provider?.provider_id,
      provider?.id,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    if (candidateIds.some((id) => enabledProviderIds.has(id))) return true;

    const candidateSlugs = [
      item?.provider_slug,
      provider?.provider_slug,
      item?.providerName,
      provider?.name,
    ]
      .map((value) =>
        String(value || "")
          .toLowerCase()
          .trim()
          .replace(/[^\w\s-]/g, "")
          .replace(/[\s_-]+/g, "-")
          .replace(/^-+|-+$/g, "")
      )
      .filter(Boolean);

    if (candidateSlugs.some((slug) => enabledProviderSlugs.has(slug))) return true;

    const candidateNames = [item?.providerName, item?.provider_name, provider?.name]
      .map((value) => String(value || "").toLowerCase().trim())
      .filter(Boolean);

    if (candidateNames.some((name) => enabledProviderNames.has(name))) return true;

    // If API payload has no provider metadata, don't hide the result blindly.
    return !candidateIds.length && !candidateSlugs.length && !candidateNames.length;
  };

  const getSearchSuggestions = async () => {
    if (searchStr.length >= 2) {
      const response = await api.get("/games/search-suggestions", {
        query: searchStr,
        providers: props.selectedProviders,
        extensiveInfo: true,
      });
      if (response.status === 200) {
        const apiItems = Array.isArray(response.data) ? response.data : [];
        setSearchSuggestions(apiItems.filter(isEnabledProviderSuggestion));
      }
    } else {
      setSearchSuggestions([]);
    }
  };

  const closeSuggestions = useCallback(() => {
    setSearchSuggestions([]);
    setHighlightedIndex(-1);
  }, []);

  const openGame = useCallback(
    (item) => {
      setSearchStr("");
      closeSuggestions();
      history.push("/play/" + item.slug);
    },
    [closeSuggestions, history]
  );

  const runFullSearch = useCallback(() => {
    const q = searchStr.trim();
    if (q.length < 2) return;
    props.setSearchStr(q);
    closeSuggestions();
    setSearchStr("");
    history.push("/games/search");
  }, [closeSuggestions, history, props, searchStr]);

  useEffect(() => {
    getSearchSuggestions();
  }, [searchStr]);

  useEffect(() => {
    if (!searchSuggestions.length) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex((prev) =>
      prev >= 0 && prev < searchSuggestions.length ? prev : 0
    );
  }, [searchSuggestions]);

  useEffect(() => {
    if (highlightedIndex < 0 || !suggestionsListRef.current) return;
    const row = suggestionsListRef.current.querySelector(
      `[data-suggestion-index="${highlightedIndex}"]`
    );
    row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [highlightedIndex]);

  // Category quick-links: match the query against category labels and their
  // BO-entered search synonyms. Purely client-side over the loaded payload.
  // panelDismissed keeps an explicit close (Escape/outside click) closed even
  // though categoryMatches derives from the unchanged searchStr; any edit of
  // the query re-opens.
  const [panelDismissed, setPanelDismissed] = useState(false);
  useEffect(() => {
    setPanelDismissed(false);
  }, [searchStr]);
  const categoryMatches = panelDismissed ? [] : searchCategories(props.categories, searchStr);
  const dropdownOpen = !!(searchSuggestions.length || categoryMatches.length);

  const closePanel = useCallback(() => {
    closeSuggestions();
    setPanelDismissed(true);
  }, [closeSuggestions]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        closePanel();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [dropdownOpen, closePanel]);

  const onInputKeyDown = (e) => {
    if (!searchSuggestions.length) {
      if (e.key === "Escape" && dropdownOpen) {
        e.preventDefault();
        closePanel();
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        runFullSearch();
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      closePanel();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => {
        const next = i < 0 ? 0 : Math.min(i + 1, searchSuggestions.length - 1);
        return next;
      });
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(0, (i < 0 ? 0 : i) - 1));
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const item = searchSuggestions[highlightedIndex];
      if (item) {
        openGame(item);
      } else {
        runFullSearch();
      }
    }
  };

  const rootClass = ["search-box-area", props.className].filter(Boolean).join(" ");

  return (
    <div
      ref={rootRef}
      className={
        rootClass + (dropdownOpen ? " search-box-area--open" : "")
      }
    >
      <div
        onClick={() => {
          props.setIsSidebarCollapsed?.(false);
        }}
        className={
          "searchbar_searchbox" +
          (dropdownOpen ? " is-open" : "")
        }
      >
        <input
          ref={searchTextBox}
          placeholder={__("Search...")}
          autoComplete="off"
          value={searchStr}
          onChange={(ev) => setSearchStr(ev.target.value)}
          onKeyDown={onInputKeyDown}
          type="search"
          enterKeyHint="search"
          aria-autocomplete="list"
          aria-expanded={searchSuggestions.length > 0}
          aria-controls="game-search-suggestions"
          role="combobox"
        />

        <span className="search-icon" aria-hidden="true">
          <i className="fas fa-search" />
        </span>

        <Collapse in={dropdownOpen}>
          <div className="search_container">
            <div className="caretUp" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6.1018 16.9814C5.02785 16.9814 4.45387 15.7165 5.16108 14.9083L10.6829 8.59762C11.3801 7.80079 12.6197 7.80079 13.3169 8.59762L18.8388 14.9083C19.5459 15.7165 18.972 16.9814 17.898 16.9814H6.1018Z"
                  fill="currentColor"
                />
              </svg>
            </div>

            {categoryMatches.length > 0 && (
              <div className="search_category_matches">
                {categoryMatches.map((c) => (
                  <Link
                    key={c.api_slug}
                    hrefLang={Translator.getSelectedLanguage().code}
                    to={"/games/" + (c.route_slug || c.api_slug)}
                    className="search_category_chip"
                    onClick={() => {
                      setSearchStr("");
                      closeSuggestions();
                    }}
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            )}

            <ul
              ref={suggestionsListRef}
              className="search_suggestions"
              id="game-search-suggestions"
              role="listbox"
              aria-label={__("Game search results")}
            >
              {searchSuggestions.map((item, index) => (
                <li key={item.id ?? item.slug ?? index} role="none">
                  <Link
                    hrefLang={Translator.getSelectedLanguage().code}
                    to={"/play/" + item.slug}
                    role="option"
                    aria-selected={highlightedIndex === index}
                    data-suggestion-index={index}
                    className={
                      "search_suggestion_row" +
                      (highlightedIndex === index ? " is-active" : "")
                    }
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onFocus={() => setHighlightedIndex(index)}
                    onClick={() => {
                      setSearchStr("");
                      closeSuggestions();
                    }}
                  >
                    <span className="search_suggestion_thumb">
                      <img
                        alt=""
                        src={item.game_snapshot_url}
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_GAME_IMAGE;
                        }}
                      />
                    </span>
                    <span className="search_suggestion_title">{item.game_name}</span>
                  </Link>
                </li>
              ))}
            </ul>

            {searchStr.trim().length >= 2 && (
              <button
                type="button"
                className="search_see_all_btn"
                onClick={() => runFullSearch()}
              >
                {__("See all results")}
              </button>
            )}
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default SearchBox;
